'use client';

import { useTranslations } from 'next-intl';
import { useLocale } from 'next-intl';
import QRCode from 'react-qr-code';

import { formatDate } from '@/lib/format';
import { PrintPortal } from '@/components/print-portal';
import {
  PrintSection,
  PrintGrid,
  PrintField
} from '@/components/print-helpers';
import type { Shipment } from '../types';

interface ShipmentPrintReceiptProps {
  shipment: Shipment;
  visible: boolean;
}

export function ShipmentPrintReceipt({
  shipment,
  visible
}: ShipmentPrintReceiptProps) {
  const t = useTranslations('shipping');
  const locale = useLocale();

  if (!visible) return null;

  return (
    <PrintPortal>
      <div
        style={{
          fontFamily: 'Arial, sans-serif',
          padding: '30px',
          maxWidth: '800px',
          margin: '0 auto'
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '20px'
          }}
        >
          <div>
            <h1 style={{ fontSize: '18px', fontWeight: 700, margin: 0 }}>
              {t('manifest')}
            </h1>
            <p
              style={{
                fontSize: '13px',
                color: '#555',
                margin: '4px 0 0'
              }}
            >
              #{shipment.shipment_number}
            </p>
          </div>
          {shipment.qr_code && (
            <div style={{ textAlign: 'center' }}>
              <QRCode value={shipment.qr_code} size={80} level='M' />
            </div>
          )}
        </div>

        <PrintSection title={t('detailShipmentInfo')}>
          <PrintGrid>
            <PrintField
              label={t('shipmentNumber')}
              value={`#${shipment.shipment_number}`}
            />
            <PrintField
              label={t('shipmentDate')}
              value={(() => {
                const d = new Date(
                  shipment.shipment_date ?? shipment.created_at
                );
                return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
              })()}
            />
            <PrintField
              label={t('location')}
              value={shipment.location || '—'}
            />
            <PrintField
              label={t('boxCount')}
              value={String(shipment.box_count)}
            />
            <PrintField
              label={t('driverColumn')}
              value={shipment.driver?.name || '—'}
            />
            <PrintField
              label={t('assignedEmployee')}
              value={shipment.assigned_employee || '—'}
            />
          </PrintGrid>
        </PrintSection>

        {shipment.notes && (
          <PrintSection title={t('notes')}>
            <p style={{ fontSize: '12px', margin: 0 }}>{shipment.notes}</p>
          </PrintSection>
        )}

        {shipment.boxes && shipment.boxes.length > 0 && (
          <PrintSection
            title={`${t('detailBoxes')} (${t('totalBoxesCount', { count: shipment.boxes.length })})`}
          >
            {shipment.boxes.map((box) => {
              const registrations = box.registrations ?? [];
              return (
                <div
                  key={box.id}
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    marginBottom: '12px'
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '6px 12px',
                      background: '#f3f4f6',
                      borderBottom:
                        registrations.length > 0 ? '1px solid #e5e7eb' : 'none'
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 700,
                        fontSize: '12px',
                        fontFamily: 'monospace'
                      }}
                    >
                      {t('boxNumber')}: {box.box_number}
                    </span>
                    <span style={{ fontSize: '11px', color: '#6b7280' }}>
                      {registrations.length}
                    </span>
                  </div>
                  {registrations.length > 0 ? (
                    <table
                      style={{
                        width: '100%',
                        fontSize: '11px',
                        borderCollapse: 'collapse'
                      }}
                    >
                      <thead>
                        <tr style={{ background: '#fff' }}>
                          <th
                            style={{
                              padding: '4px 12px',
                              textAlign: 'start',
                              fontWeight: 600,
                              color: '#374151',
                              borderBottom: '1px solid #e5e7eb'
                            }}
                          >
                            {t('printContainerNumber')}
                          </th>
                          <th
                            style={{
                              padding: '4px 12px',
                              textAlign: 'end',
                              fontWeight: 600,
                              color: '#374151',
                              borderBottom: '1px solid #e5e7eb'
                            }}
                          >
                            {t('printContainerType')}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {registrations.map((reg, idx) => (
                          <tr
                            key={reg.id}
                            style={{
                              background: idx % 2 === 0 ? '#fff' : '#f9fafb'
                            }}
                          >
                            <td
                              style={{
                                padding: '4px 12px',
                                fontFamily: 'monospace'
                              }}
                            >
                              {reg.container_number}
                            </td>
                            <td
                              style={{ padding: '4px 12px', textAlign: 'end' }}
                            >
                              {reg.container_type?.name ?? '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p
                      style={{
                        padding: '8px 12px',
                        fontSize: '11px',
                        color: '#9ca3af',
                        margin: 0
                      }}
                    >
                      {t('printNoContainersForBox')}
                    </p>
                  )}
                </div>
              );
            })}
          </PrintSection>
        )}

        <PrintSection title={t('detailMeta')}>
          <PrintGrid>
            {shipment.creator && (
              <PrintField
                label={t('detailCreatedBy')}
                value={shipment.creator.name}
              />
            )}
            <PrintField
              label={t('detailCreatedAt')}
              value={formatDate(
                shipment.created_at,
                { hour: 'numeric', minute: 'numeric' },
                locale
              )}
            />
            <PrintField
              label={t('detailUpdatedAt')}
              value={formatDate(
                shipment.updated_at,
                { hour: 'numeric', minute: 'numeric' },
                locale
              )}
            />
          </PrintGrid>
        </PrintSection>

        {/* Signature blocks */}
        <div
          style={{
            display: 'flex',
            gap: '40px',
            marginTop: '32px',
            fontFamily: 'Arial, sans-serif',
            fontSize: '13px'
          }}
        >
          {/* Shipment Registrar */}
          {/* <div style={{ flex: 1 }}>
            <div
              style={{
                fontWeight: 'bold',
                marginBottom: '16px',
                textAlign: 'center',
                borderBottom: '2px solid #333',
                paddingBottom: '4px'
              }}
            >
              Shipment Registrar / مسجل الشحنة
            </div>
            {[
              ['Name', 'الاسم'],
              ['Date', 'التاريخ'],
              ['Signature', 'التوقيع']
            ].map(([en, ar]) => (
              <div
                key={en}
                style={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: '8px',
                  marginBottom: '20px'
                }}
              >
                <span style={{ whiteSpace: 'nowrap', minWidth: '100px' }}>
                  {en} / {ar}:
                </span>
                <div
                  style={{
                    flex: 1,
                    borderBottom: '1px solid #333',
                    height: '20px'
                  }}
                />
              </div>
            ))}
          </div> */}

          {/* Driver */}
          {/* <div style={{ flex: 1 }}>
            <div
              style={{
                fontWeight: 'bold',
                marginBottom: '16px',
                textAlign: 'center',
                borderBottom: '2px solid #333',
                paddingBottom: '4px'
              }}
            >
              Driver / السائق
            </div>
            {[
              ['Name', 'الاسم'],
              ['Date', 'التاريخ'],
              ['Signature', 'التوقيع']
            ].map(([en, ar]) => (
              <div
                key={en}
                style={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: '8px',
                  marginBottom: '20px'
                }}
              >
                <span style={{ whiteSpace: 'nowrap', minWidth: '100px' }}>
                  {en} / {ar}:
                </span>
                <div
                  style={{
                    flex: 1,
                    borderBottom: '1px solid #333',
                    height: '20px'
                  }}
                />
              </div>
            ))}
          </div> */}
        </div>

        {/* OMA-523: Final Declarations & Procedures of Reception & Delivery
            (image 6.6) — two-party acknowledgment table: the party handing the
            shipment over and the party receiving it. All fields are blank and
            filled by hand on print. */}
        <FinalDeclarationsTable />
      </div>
    </PrintPortal>
  );
}

const RED_HEADER_STYLE = {
  background: '#b91c1c',
  color: '#ffffff',
  padding: '8px 12px',
  fontSize: '13px',
  fontWeight: 700,
  border: '1px solid #999',
  textAlign: 'center' as const
};

const SUB_HEADER_STYLE = {
  background: '#fce7f3',
  padding: '6px 8px',
  fontSize: '12px',
  fontWeight: 700,
  border: '1px solid #999',
  textAlign: 'center' as const,
  color: '#7f1d1d'
};

const LABEL_CELL_STYLE = {
  padding: '8px 10px',
  fontSize: '12px',
  border: '1px solid #999',
  // Taller than the original 26px: dropping the declaration paragraph freed
  // vertical space, and these rows are written on by hand.
  height: '34px',
  verticalAlign: 'top' as const,
  textAlign: 'right' as const,
  direction: 'rtl' as const
};

function FinalDeclarationsTable() {
  // Two columns ordered visually in RTL (right → left): the party the shipment
  // is received FROM, then the party it is delivered TO. We use direction:rtl
  // on the table so the first <th>/<td> renders on the right.
  //
  // OMA-596 dropped the middle "مندوب ماجستك العالمية" column and the
  // hand-written declaration paragraph that used to sit between the identity
  // rows and the signature rows — both were struck out on the reviewed print.
  const fieldRowLabels = ['الاسم', 'الوظيفة', 'الجهة'];
  const signatureRowLabels = ['التاريخ', 'الزمن', 'التوقيع', 'الختم'];

  return (
    <div style={{ marginTop: '32px', direction: 'rtl' }}>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontFamily: 'Arial, sans-serif',
          tableLayout: 'fixed'
        }}
      >
        <thead>
          <tr>
            <th colSpan={2} style={RED_HEADER_STYLE}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '12px'
                }}
              >
                <span>د – إقرارات وإجراءات الاستلام والتسليم النهائية</span>
                <span style={{ fontSize: '11px', fontWeight: 600 }}>
                  Final Declarations &amp; Procedures of Reception &amp;
                  Delivery
                </span>
              </div>
            </th>
          </tr>
          <tr>
            <th style={SUB_HEADER_STYLE}>خاص بالجهة المستلم منها الاستلام</th>
            <th style={SUB_HEADER_STYLE}>
              خاص بالجهة المستلم إليها &quot; التسليم &quot;
            </th>
          </tr>
        </thead>
        <tbody>
          {[...fieldRowLabels, ...signatureRowLabels].map((label) => (
            <tr key={label}>
              <td style={LABEL_CELL_STYLE}>{label}</td>
              <td style={LABEL_CELL_STYLE}>{label}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
